"use client";

import { useEffect } from "react";

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === "ChunkLoadError" ||
    /Loading chunk .* failed|Loading CSS chunk .* failed|Failed to fetch dynamically imported module/i.test(
      error.message ?? ""
    )
  );
}

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("Application error:", error);

    // A ChunkLoadError means this tab is running HTML from a build that has
    // since been replaced — the referenced JS chunk no longer exists on the
    // CDN. Reloading pulls the current build. Guard with sessionStorage so a
    // genuinely broken deploy can't put us in a reload loop.
    if (isChunkLoadError(error) && typeof window !== "undefined") {
      const key = "chunk-reload";
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
      }
    }
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 text-center">
      <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
        Something went wrong
      </h1>
      <p className="mt-4 text-gray-600 dark:text-gray-400">
        An unexpected error occurred. Please try again.
      </p>
      <button
        onClick={reset}
        className="mt-8 inline-flex items-center rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2"
      >
        Try again
      </button>
    </div>
  );
}
